import React, { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import DonationButton from './DonationButton';
import { Link, useLocation } from 'react-router-dom';
import { IoGlasses } from "react-icons/io5";
import Footer from './Footer';

function CompressPdf() {
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compressionLevel, setCompressionLevel] = useState('medium');
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const location = useLocation();
  const isMainPage = location.pathname === '/';

  const compressionSettings = {
    low: {
      useObjectStreams: true,
      updateMetadata: true,
      compress: true,
      objectsPerStream: 50
    },
    medium: {
      useObjectStreams: true,
      updateMetadata: true,
      compress: true,
      objectsPerStream: 25,
      preserveImageQuality: 0.8
    },
    high: {
      useObjectStreams: true,
      updateMetadata: true,
      compress: true,
      objectsPerStream: 10,
      preserveImageQuality: 0.6,
      compressImages: true,
      imageQuality: 'medium'
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setOriginalSize(file.size);
    const reader = new FileReader();
    reader.onload = async (e) => {
      setPdf({
        name: file.name,
        data: new Uint8Array(e.target.result)
      });
    };
    reader.readAsArrayBuffer(file);
  };

  const compressPDF = async () => {
    if (!pdf) return;
    
    setLoading(true);
    try {
      // Загружаем исходный PDF документ
      const pdfDoc = await PDFDocument.load(pdf.data);
      
      let compressedPdfBytes;

      if (compressionLevel === 'low') {
        // 1 проход: только настройки low
        compressedPdfBytes = await pdfDoc.save(compressionSettings.low);
      } else if (compressionLevel === 'medium') {
        // 3 прохода: low -> medium -> high
        const pass1Bytes = await pdfDoc.save(compressionSettings.low);
        const pdfDoc2 = await PDFDocument.load(pass1Bytes);
        const pass2Bytes = await pdfDoc2.save(compressionSettings.medium);
        const pdfDoc3 = await PDFDocument.load(pass2Bytes);
        compressedPdfBytes = await pdfDoc3.save(compressionSettings.high);
      } else if (compressionLevel === 'high') {
        // 4 прохода: low -> medium -> high -> дополнительная оптимизация + high
        const pass1Bytes = await pdfDoc.save(compressionSettings.low);
        const pdfDoc2 = await PDFDocument.load(pass1Bytes);
        const pass2Bytes = await pdfDoc2.save(compressionSettings.medium);
        const pdfDoc3 = await PDFDocument.load(pass2Bytes);
        const pass3Bytes = await pdfDoc3.save(compressionSettings.high);
        const pdfDoc4 = await PDFDocument.load(pass3Bytes);
        
        // Дополнительная оптимизация: более сильное уменьшение размеров страниц
        const pages = pdfDoc4.getPages();
        for (const page of pages) {
          const { width, height } = page.getSize();
          // Уменьшаем размер страницы до 85% от исходного (более агрессивно, чем 90%)
          page.setSize(width * 0.85, height * 0.85);
        }
        
        compressedPdfBytes = await pdfDoc4.save(compressionSettings.high);
      }
      
      setCompressedSize(compressedPdfBytes.length);
      
      const blob = new Blob([compressedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `compressed_${pdf.name}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error compressing PDF:', error);
    }
    setLoading(false);
  };

  const compressionPercent = compressedSize ? 
    Math.round((1 - compressedSize / originalSize) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <header className="text-center">
        <Link to="/pdf-tools" className="text-4xl font-bold text-black">
          <div className="flex items-center gap-2 justify-center">
            <IoGlasses size={50} />
            PDF llama
          </div>
        </Link>
        {!isMainPage && (
          <div className="mt-1">
            <Link to="/pdf-tools" className="text-gray-600 hover:text-gray-800">
              Go to another operations with PDF
            </Link>
          </div>
        )}
      </header>

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 mt-2">
        <h1 className="text-3xl font-bold mb-6">Compress PDF</h1>
        
        <input
          type="file"
          accept=".pdf"
          onChange={handlePdfUpload}
          className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
        />

        {pdf && (
          <>
            <div className="mb-6">
              <p className="text-gray-700 mb-2">Compression level:</p>
              <div className="flex justify-between items-center">
                <label className="flex-1 text-center">
                  <input
                    type="radio"
                    name="compression"
                    value="low"
                    checked={compressionLevel === 'low'}
                    onChange={(e) => setCompressionLevel(e.target.value)}
                    className="mr-2"
                  />
                  Low
                </label>
                <label className="flex-1 text-center">
                  <input
                    type="radio"
                    name="compression"
                    value="medium"
                    checked={compressionLevel === 'medium'}
                    onChange={(e) => setCompressionLevel(e.target.value)}
                    className="mr-2"
                  />
                  Medium
                </label>
                <label className="flex-1 text-center">
                  <input
                    type="radio"
                    name="compression"
                    value="high"
                    checked={compressionLevel === 'high'}
                    onChange={(e) => setCompressionLevel(e.target.value)}
                    className="mr-2"
                  />
                  High
                </label>
              </div>
            </div>

            {compressedSize > 0 && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p>Original size: {(originalSize / 1024 / 1024).toFixed(2)} MB</p>
                <p>Compressed size: {(compressedSize / 1024 / 1024).toFixed(2)} MB</p>
                <p>Compression ratio: {compressionPercent}%</p>
              </div>
            )}

            <button
              onClick={compressPDF}
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg mb-6 hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Compressing...' : 'Compress PDF'}
            </button>
          </>
        )}

      </div>
      <div className="mt-8">
        <DonationButton />
      </div>

      <article className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-6">
          Compress PDF online
        </h1>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            Free online PDF compressor without quality loss
          </h2>
          <p className="mb-4">
            PDF llama compressor tool helps you compress large PDF files while maintaining document 
            clarity and resolution. 
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            How to compress PDF files online
          </h2>
          <div className="space-y-4">
            <h3 className="text-xl font-medium">Simple steps to reduce PDF size:</h3>
            <div className="pl-4">
              <p className="mb-2">
                1. Upload your PDF file to our online compressor
              </p>
              <p className="mb-2">
                2. Choose your preferred compression level
              </p>
              <p className="mb-2">
                3. Wait for the compression process to complete
              </p>
              <p className="mb-2">
                4. Download your compressed PDF file
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            Advanced PDF compression features
          </h2>
          <div className="space-y-4">
            <h3 className="text-xl font-medium">Why choose our PDF compressor:</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Free online tool with no file size limits almost
              </li>
              <li>
                Multiple compression levels to choose from
              </li>
              <li>
                Secure and instant file processing
              </li>
              <li>
                No registration required to reduce PDF size
              </li>
              <li>
                Compatible with all PDF versions
              </li>
            </ul>
          </div>
        </section>

      </article>

      <Footer />

    </div>
  );
}

export default CompressPdf;
